import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Check, Copy, FileImage, FileText, Info, Loader2, X } from "lucide-react";
import { compressReceipt, postFormWithRetry } from "@/lib/receipt";
import { api, API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import InfoGlassBadge from "./InfoGlassBadge";
import OccasionCard from "./OccasionCard";
import PremiumCard from "./PremiumCard";
import WizardHeader from "./WizardHeader";
import {
  CONSTRUCTOR_ORDER, PRICE_PER_SONG, REGEN_PRICE, SOUND_ICONS, SOUND_KEYS,
  getClientId, getTgUser, songWord, stripEmoji, tgContact, trackApp,
} from "./appData";
import "./appcreate.css";

const STEPS = ["event", "genre", "mood", "lang", "voice", "text", "pkg", "pay", "done"];
const VOICE_EMOJI = { male: "👨", female: "👩", child: "🧒", duet: "👫" };
const LANG_FLAGS = { uk: "🇺🇦", ru: "🇷🇺", en: "🇬🇧" };
const MAX_RECEIPT = 10 * 1024 * 1024;
const FALLBACK_PKG = { key: "", name_ua: "1 пісня (2 варіанти)", price_uah: 169, songs_count: 1 };

function PackageCard({ p, active, onPick }) {
  const n = Math.max(1, Number(p.songs_count) || 1);
  const price = Math.round(Number(p.price_uah) || 0);
  const save = Math.max(0, Math.round((1 - price / (n * PRICE_PER_SONG)) * 100));
  return (
    <button type="button" data-testid={`wizard-pkg-${p.key || "fallback"}`} onClick={() => onPick(p.key)}
      className={"pkg-card" + (p.is_popular ? " popular" : "") + (active ? " active" : "")}>
      <span className="pkg-price">{price} ₴</span>
      <span className="pkg-name">{p.emoji ? p.emoji + " " : ""}{p.name_ua}</span>
      <span className="pkg-meta">{n} {songWord(n)} · 2 варіанти на пісню</span>
      {save > 0 && <span className="pkg-save">-{save}%</span>}
      <span className="pkg-cta">{active ? <>Обрано <Check size={14} /></> : <>Обрати <ArrowRight size={14} /></>}</span>
      <span className="pkg-note"><i />{Math.round(price / n)} ₴ / пісня</span>
    </button>
  );
}

function BalanceModal({ open, balance, busy, onClose, onConfirm }) {
  if (!open) return null;
  return createPortal(
    <div className="epw-modal-wrap" role="dialog" aria-modal="true">
      <div className="epw-modal-back" onClick={onClose} />
      <div className="epw-modal" data-testid="balance-modal">
        <div className="epw-modal-head">
          <div className="epw-modal-title">Підтвердження</div>
          <button type="button" className="epw-iconbtn" onClick={onClose} aria-label="Закрити"><X size={18} /></button>
        </div>
        <div className="epw-order-row epw-order-modal">
          <span className="epw-order-name">Пісня з балансу</span>
          <span className="epw-order-price">1 генерація</span>
        </div>
        <div className="epw-order-meta">2 варіанти на пісню · без оплати картою</div>
        <div className="epw-order-line epw-order-line-sub">
          <span className="epw-order-name">На балансі зараз</span>
          <span className="epw-order-price">{balance}</span>
        </div>
        <div className="epw-order-line epw-order-line-sub">
          <span className="epw-order-name">Залишиться після створення</span>
          <span className="epw-order-price">{Math.max(0, balance - 1)}</span>
        </div>
        <p className="epw-hint">Підтверджуєте створення пісні з балансу?</p>
        <div className="epw-modal-actions">
          <button type="button" className="epw-ghost" onClick={onClose}>Скасувати</button>
          <button type="button" data-testid="balance-confirm" onClick={onConfirm}
            disabled={!!busy} className="epw-cta">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} strokeWidth={3} />}
            Підтвердити
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ModalPager({ page, pages, onPage }) {
  if (pages <= 1) return null;
  return (
    <div className="epw-modal-pager">
      <button type="button" className="pg-btn" disabled={page <= 1}
        onClick={() => onPage(page - 1)} aria-label="Назад">←</button>
      <span className="epw-modal-page">{page} / {pages}</span>
      <button type="button" className="pg-btn" disabled={page >= pages}
        onClick={() => onPage(page + 1)} aria-label="Далі">→</button>
    </div>
  );
}

const GENRES_PER_PAGE = 8;

function ExtraGenresModal({ open, items, onPick, onClose }) {
  const [page, setPage] = useState(1);
  useEffect(() => { if (open) setPage(1); }, [open]);
  if (!open) return null;
  const pages = Math.max(1, Math.ceil(items.length / GENRES_PER_PAGE));
  const chunk = items.slice((page - 1) * GENRES_PER_PAGE, page * GENRES_PER_PAGE);
  return createPortal(
    <div className="epw-modal-wrap" role="dialog" aria-modal="true">
      <div className="epw-modal-back" onClick={onClose} />
      <div className="epw-modal epw-genre-modal" data-testid="extra-genres-modal">
        <div className="epw-modal-head">
          <div className="epw-modal-title">Додаткові жанри</div>
          <button type="button" className="epw-iconbtn" onClick={onClose} aria-label="Закрити"><X size={18} /></button>
        </div>
        <p className="epw-modal-sub">Усі стилі — оберіть один.</p>
        <div className="epw-grid-2 epw-genre-grid">
          {chunk.map((g) => (
            <button key={g.key} type="button" data-testid={`extra-genre-${g.key}`}
              className="epw-card" onClick={() => onPick(g.key)}>
              <span className="epw-card-txt">
                <span className="epw-card-t">{g.ua || g.key}</span>
              </span>
            </button>
          ))}
        </div>
        <ModalPager page={page} pages={pages} onPage={setPage} />
      </div>
    </div>,
    document.body,
  );
}

function MixGenresModal({ open, items, selected, onToggle, onConfirm, onClose }) {
  const [page, setPage] = useState(1);
  useEffect(() => { if (open) setPage(1); }, [open]);
  if (!open) return null;
  const pages = Math.max(1, Math.ceil(items.length / GENRES_PER_PAGE));
  const chunk = items.slice((page - 1) * GENRES_PER_PAGE, page * GENRES_PER_PAGE);
  const labelOf = (k) => (items.find((g) => g.key === k) || {}).ua || k;
  const ready = selected.length === 2;
  return createPortal(
    <div className="epw-modal-wrap" role="dialog" aria-modal="true">
      <div className="epw-modal-back" onClick={onClose} />
      <div className="epw-modal epw-genre-modal" data-testid="mix-genres-modal">
        <div className="epw-modal-head">
          <div className="epw-modal-title">Мікс жанрів</div>
          <button type="button" className="epw-iconbtn" onClick={onClose} aria-label="Закрити"><X size={18} /></button>
        </div>
        <p className="epw-modal-sub">
          Оберіть 2 жанри: перший стане основою (задає темп і грув), другий додасть характер.
        </p>
        {selected.length > 0 && (
          <div className="epw-mix-chips epw-mix-chips-modal" data-testid="mix-selected">
            {selected.map((k, i) => (
              <span key={k} className="epw-mix-chip">
                {i === 0 ? "1 · " : "2 · "}{labelOf(k)}
                <button type="button" aria-label="Прибрати" onClick={() => onToggle(k)}>×</button>
              </span>
            ))}
          </div>
        )}
        <div className="epw-grid-2 epw-genre-grid">
          {chunk.map((g) => {
            const idx = selected.indexOf(g.key);
            return (
              <button key={g.key} type="button" data-testid={`mix-genre-${g.key}`}
                className={"epw-card" + (idx >= 0 ? " active" : "")}
                onClick={() => onToggle(g.key)}>
                <span className="epw-card-txt">
                  <span className="epw-card-t">{g.ua || g.key}</span>
                </span>
                {idx >= 0 && <span className="epw-card-num">{idx + 1}</span>}
              </button>
            );
          })}
        </div>
        <ModalPager page={page} pages={pages} onPage={setPage} />
        <div className="epw-modal-actions">
          <button type="button" className="epw-ghost" onClick={onClose}>Скасувати</button>
          <button type="button" data-testid="mix-confirm" className="epw-cta"
            disabled={!ready} onClick={onConfirm}>
            Готово {selected.length}/2
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function CustomModal({ open, title, sub, placeholder, value, onChange, onClose, onSubmit, minLen, err, inputRef }) {
  if (!open) return null;
  const ok = value.trim().length >= minLen;
  return createPortal(
    <div className="epw-modal-wrap" role="dialog" aria-modal="true">
      <div className="epw-modal-back" onClick={onClose} />
      <div className="epw-modal" data-testid="custom-modal">
        <div className="epw-modal-head">
          <div className="epw-modal-title">{title}</div>
          <button type="button" className="epw-iconbtn" onClick={onClose} aria-label="Закрити"><X size={18} /></button>
        </div>
        <p className="epw-modal-sub">{sub}</p>
        <input ref={inputRef} value={value} maxLength={200}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && ok) onSubmit(); }}
          placeholder={placeholder} className="epw-input" />
        <p className="epw-hint">Мінімум {minLen} символи, щоб продовжити.</p>
        {err && <p className="epw-error">{err}</p>}
        <div className="epw-modal-actions">
          <button type="button" className="epw-ghost" onClick={onClose}>Скасувати</button>
          <button type="button" className="epw-cta" disabled={!ok} onClick={onSubmit}>
            Далі <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function OwnTextModal({ open, onClose, onConfirm }) {
  if (!open) return null;
  return createPortal(
    <div className="epw-modal-wrap" role="dialog" aria-modal="true">
      <div className="epw-modal-back" onClick={onClose} />
      <div className="epw-modal epw-own-text-modal" data-testid="own-text-modal">
        <div className="epw-modal-head">
          <div className="epw-modal-title">Власний текст</div>
          <button type="button" className="epw-iconbtn" onClick={onClose} aria-label="Закрити"><X size={18} /></button>
        </div>
        <div className="epw-own-alert">
          <span className="epw-own-alert-icon"><FileText size={22} /></span>
          <div className="epw-own-alert-txt">
            <b>Увага: ви переключили на власний текст.</b>
            <p>Усе, що ви напишете, буде проспівано дослівно. Не додавайте службових фраз на кшталт «текст пісні» або «створи пісню» — вони теж потраплять у пісню.</p>
          </div>
        </div>
        <div className="epw-modal-actions">
          <button type="button" className="epw-cta" data-testid="own-text-confirm" onClick={onConfirm}>
            Підтверджую
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function PkgModal({ pkg, regen, onRegen, onClose, onProceed }) {
  if (!pkg) return null;
  const n = Math.max(1, Number(pkg.songs_count) || 1);
  const price = Math.round(Number(pkg.price_uah) || 0);
  return createPortal(
    <div className="epw-modal-wrap" role="dialog" aria-modal="true">
      <div className="epw-modal-back" onClick={onClose} />
      <div className="epw-modal" data-testid="pkg-modal">
        <div className="epw-modal-head">
          <div className="epw-modal-title">Ваше замовлення</div>
          <button type="button" className="epw-iconbtn" onClick={onClose} aria-label="Закрити"><X size={18} /></button>
        </div>
        <div className="epw-order-row epw-order-modal">
          <span className="epw-order-name">{pkg.emoji ? pkg.emoji + " " : ""}{pkg.name_ua}</span>
          <span className="epw-order-price">{price} ₴</span>
        </div>
        <div className="epw-order-meta">{n} {songWord(n)} · 2 варіанти на пісню</div>
        <div className="epw-divider-label"><span className="epw-regen-badge">рекомендуємо</span></div>
        <div className="epw-order-row epw-order-regen">
          <span className="epw-order-name">Перегенерація</span>
          <span className="epw-order-price">+{REGEN_PRICE} ₴</span>
        </div>
        <div className="epw-regen-row">
          <div className="epw-regen-d">Це дозволить вам після генерації переспівати пісню, внести правки в текст.</div>
          <button type="button" role="switch" aria-checked={regen} data-testid="pkg-modal-regen"
            onClick={() => onRegen(!regen)}
            className={"epw-switch" + (regen ? " on" : "")}><i /></button>
        </div>
        <div className="epw-modal-actions">
          <button type="button" data-testid="pkg-modal-proceed" onClick={onProceed} className="epw-cta">
            Перейти до сплати <img className="epw-btn-ico" src="/design/icons-lg/forward.svg" alt="" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function AppCreateWizard({ initialEvent = "", initialPackage = "", initialDraft = null,
  initialTopup = false,
  balance = 0, onBalanceUsed, onClose, onOrdered, onGoSongs }) {
  const { t } = useLang();
  const c = t.create;
  const d = initialDraft || {};

  // Стиль из приклада звучання: жанр/настрій уже выбраны админом — эти шаги
  // пропускаем (только для драфта без заказа — у черновиков заказов они остаются).
  const skipGenre = !d.id && !!d.genre;
  const skipMood = !d.id && !!d.mood;
  const resolveNext = (to) => {
    if (to === "genre" && skipGenre) to = "mood";
    if (to === "mood" && skipMood) to = "lang";
    return to;
  };
  const resolveBack = (to) => {
    if (to === "mood" && skipMood) to = "genre";
    if (to === "genre" && skipGenre) to = "event";
    return to;
  };

  const [page, setPage] = useState(
    initialTopup ? "pkg"
      : d.lyrics_state === "generating" ? "text"
        : (d.id ? "pkg" : (initialEvent && initialEvent !== "custom" ? "genre" : "event")));
  const [cat, setCat] = useState(null);
  const [event, setEvent] = useState(d.event || initialEvent || "");
  const [eventText, setEventText] = useState(d.event_text || "");
  const [genre, setGenre] = useState(d.genre || "");
  const [genreText, setGenreText] = useState("");
  const [genreMix, setGenreMix] = useState(() => (d.genre_mix || []).slice(0, 2));
  const [extraOpen, setExtraOpen] = useState(false);
  const [mixOpen, setMixOpen] = useState(false);
  const [mixDraft, setMixDraft] = useState([]);
  const [mood, setMood] = useState(d.mood || "");
  const [moodText, setMoodText] = useState(d.mood_text || "");
  const [lang, setLang] = useState(d.song_language || "");
  const [voice, setVoice] = useState(d.voice || "female");
  const [desc, setDesc] = useState((d.ready_lyrics && d.ready_lyrics.trim()) || d.description || "");
  const [story, setStory] = useState(() => (d.description || "").trim());
  const [textReady, setTextReady] = useState(!!((d.ready_lyrics || "").trim().length >= 25));
  const [generated, setGenerated] = useState(!!((d.ready_lyrics || "").trim().length >= 25));
  const [genLeft, setGenLeft] = useState(null);
  const [variants, setVariants] = useState(() => (d.lyrics_variants || []).slice(0, 2));
  const [pickIdx, setPickIdx] = useState(() => Number(d.lyrics_pick) || 0);
  const [modal, setModal] = useState(initialEvent === "custom" && !d.id ? "event" : null);
  const modalInputRef = useRef(null);

  useEffect(() => {
    const on = !!modal;
    document.body.classList.toggle("epw-modal-open", on);
    if (!on) return;
    const t = setTimeout(() => {
      try { modalInputRef.current?.focus({ preventScroll: true }); } catch {}
    }, 260);
    return () => clearTimeout(t);
  }, [modal]);
  useEffect(() => () => document.body.classList.remove("epw-modal-open"), []);
  useEffect(() => {
    const toTop = () => {
      try { document.activeElement?.blur?.(); } catch {}
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      document.querySelector(".mobile-content")?.scrollTo?.(0, 0);
    };
    toTop();
    const raf = requestAnimationFrame(toTop);
    const t = setTimeout(toTop, 90);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [page]);
  const [draftTitle, setDraftTitle] = useState("");
  const [busy, setBusy] = useState("");
  const [uploadPct, setUploadPct] = useState(null);
  const [err, setErr] = useState("");
  const [packages, setPackages] = useState([]);
  const [packagesReady, setPackagesReady] = useState(false);
  const [pkgKey, setPkgKey] = useState(initialPackage || "");
  const [regen, setRegen] = useState(false);
  const [pkgModal, setPkgModal] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [card, setCard] = useState("");
  const [cardLoading, setCardLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [preview, setPreview] = useState("");
  const [contact, setContact] = useState(() => tgContact(getTgUser()));
  const [orderNumber, setOrderNumber] = useState(null);
  const fileRef = useRef(null);
  const advanceRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && busy !== "submit") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  useEffect(() => {
    api.get("/public/constructor").then((r) => setCat(r.data || {})).catch(() => setCat({}));
  }, []);

  const loadPackages = () => {
    setPackagesReady(false);
    api.get("/public/packages")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const items = list.filter((p) => p && p.key);
        setPackages(items);
        setPkgKey((k) => k || "");
      })
      .catch(() => setPackages([]))
      .finally(() => setPackagesReady(true));
  };

  useEffect(() => {
    loadPackages();
  }, []);

  const topupOpenedRef = useRef(false);
  useEffect(() => {
    if (!initialTopup || topupOpenedRef.current || !packagesReady) return;
    const p = packages.find((x) => x.key === initialPackage) || packages[0];
    if (!p) return;
    topupOpenedRef.current = true;
    setPkgKey(p.key);
    setPkgModal(p);
  }, [initialTopup, packagesReady, packages, initialPackage]);

  const submittedRef = useRef(false);
  const buildDraftBody = (notify = false) => {
    if (initialTopup || submittedRef.current) return null;
    const cid = getClientId();
    // В description всегда уходит ИСТОРИЯ юзера — не сгенерированный текст.
    const storyText = (story.trim() || desc.trim());
    if (!cid || storyText.length < 25) return null;
    let initData = "";
    try { initData = window.Telegram?.WebApp?.initData || ""; } catch { /* noop */ }
    return {
      notify,
      genre: genre || "pop",
      voice: voice || "female",
      description: (styleNote ? styleNote + ". " : "") + storyText,
      client_id: cid,
      mode: "app",
      event: event || "",
      event_text: eventText || "",
      mood: mood || "",
      mood_text: mood === "custom" ? moodText.trim() : "",
      genre_text: genre === "custom" ? genreText.trim() : "",
      genre_mix: genre === "mix" ? genreMix : [],
      song_language: lang || "uk",
      tg_id: getTgUser()?.id || null,
      init_data: initData,
      order_id: draftOrderRef.current || initialDraft?.id || null,
      ready_lyrics: textReady ? desc.trim() : "",
      ready_title: generated ? draftTitle : "",
    };
  };
  const saveDraft = (notify = false) => {
    const body = buildDraftBody(notify);
    if (!body) return;
    api.post("/public/landing/unpaid", body)
      .then((r) => { if (r.data?.id) draftOrderRef.current = r.data.id; })
      .catch(() => {});
  };
  const beaconRef = useRef(null);
  beaconRef.current = () => {
    // Закрытие апки: sendBeacon не отменяется при выгрузке страницы.
    const body = buildDraftBody(false);
    if (!body) return;
    try {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      navigator.sendBeacon(`${API}/public/landing/unpaid`, blob);
    } catch {
      saveDraft(false);
    }
  };
  const draftOrderRef = useRef(initialDraft?.id || null);
  const saveDraftRef = useRef(saveDraft);
  saveDraftRef.current = saveDraft;

  useEffect(() => {
    // Черновик пишем на шаге тарифа, а карточку в чат заказов — только когда
    // юзер реально дошёл до оплаты (увидел карту).
    if (page === "pkg") saveDraftRef.current(false);
    if (page === "pay") saveDraftRef.current(true);
  }, [page]);

  useEffect(() => () => saveDraftRef.current(false), []);

  useEffect(() => {
    // Telegram mini-app закрывается/сворачивается — React-unmount не сработает,
    // поэтому сохраняем черновик: свёртывание — axios, закрытие — sendBeacon.
    const onHide = () => {
      if (document.visibilityState === "hidden") saveDraftRef.current(false);
    };
    const onPageHide = () => { if (beaconRef.current) beaconRef.current(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  useEffect(() => {
    // Битый черновик: микс без 2 участников — возвращаем юзера к выбору жанров,
    // а не пропускаем к оплате (иначе уедет «modern pop» вместо микса).
    if (genre === "mix" && genreMix.length < 2) {
      setMixDraft(genreMix);
      setPage("genre");
      setMixOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const d0 = initialDraft;
    if (!d0?.id || d0.lyrics_state !== "generating") return undefined;
    let alive = true;
    setBusy("gen-text");
    (async () => {
      for (let i = 0; i < 48; i += 1) {
        await new Promise((r) => setTimeout(r, 2500));
        if (!alive) return;
        try {
          const r = await api.get("/public/app/gen-lyrics/status", { params: { order_id: d0.id } });
          if (r.data?.state === "ready") {
            applyStatus(r.data);
            if (alive) setBusy("");
            return;
          }
          if (r.data?.state === "error") {
            if (alive) { setBusy(""); setErr("Не вдалося згенерувати текст. Спробуйте ще раз."); }
            return;
          }
        } catch { /* keep polling */ }
      }
      if (alive) setBusy("");
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (page !== "pay" || card || cardLoading) return;
    setCardLoading(true);
    api.get("/public/payment-card")
      .then((r) => setCard(r.data?.card || ""))
      .catch(() => setCard(""))
      .finally(() => setCardLoading(false));
  }, [page, card, cardLoading]);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  useEffect(() => () => clearTimeout(advanceRef.current), []);

  const allEvents = cat?.events || [];
  const orderedEvents = [...allEvents].sort((a, b) => {
    const ai = CONSTRUCTOR_ORDER.indexOf(a.key);
    const bi = CONSTRUCTOR_ORDER.indexOf(b.key);
    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
  });
  const events = orderedEvents;
  const moods = cat?.moods || [];
  const langs = cat?.langs || [];
  const voices = cat?.voices || [];

  const stepIdx = STEPS.indexOf(page);
  const hasBalance = Number(balance) > 0;
  const minLen = textReady ? 40 : 50;
  const selectedPkg = packages.find((p) => p.key === pkgKey) || null;
  const activePkg = selectedPkg || (packagesReady && packages.length === 0 ? FALLBACK_PKG : null);
  const total = (activePkg ? Number(activePkg.price_uah) || 0 : 0) + (regen ? REGEN_PRICE : 0);
  const styleNote = [
    genre === "custom" && genreText.trim() ? `Свій стиль: ${genreText.trim()}` : "",
    mood === "custom" && moodText.trim() ? `Свій настрій: ${moodText.trim()}` : "",
  ].filter(Boolean).join(". ");

  const catGenreList = [...(cat?.genres_main || []), ...(cat?.genres_extra || [])]
    .filter((g, i, arr) => arr.findIndex((x) => x.key === g.key) === i);

  const canNext = {
    event: !!event && (event !== "custom" || eventText.trim().length >= 3),
    genre: genre === "custom" ? genreText.trim().length >= 3
      : (genre === "mix" ? genreMix.length === 2
        : (SOUND_KEYS.includes(genre) || catGenreList.some((g) => g.key === genre))),
    mood: mood === "custom" ? moodText.trim().length >= 2 : moods.some((m) => m.key === mood),
    lang: langs.some((l) => l.key === lang),
    voice: voices.some((v) => v.key === voice),
    text: desc.trim().length >= minLen,
  }[page];

  const later = (fn) => {
    clearTimeout(advanceRef.current);
    advanceRef.current = setTimeout(fn, 200);
  };

  const notifyManualDescription = (text) => {
    const value = (text || "").trim();
    if (value.length < 25) return;
    let initData = "";
    try { initData = window.Telegram?.WebApp?.initData || ""; } catch { /* noop */ }
    api.post("/public/description", {
      description: value,
      tg_id: getTgUser()?.id || null,
      client_id: getClientId(),
      init_data: initData,
      source: "міні-апп",
    }).catch(() => {});
  };

  const nextPage = () => {
    clearTimeout(advanceRef.current);
    setErr("");
    // Лог в чат сайта: юзер прошёл этап истории (+ сама история).
    if (page === "text") {
      const full = desc.trim();
      if (!generated) notifyManualDescription(full);
      const label = generated
        ? "пройшов етап історії (згенерований текст)"
        : (textReady ? "пройшов етап історії (свій текст)" : "пройшов етап історії");
      trackApp(label, full.slice(0, 800));
    }
    setPage((p) => resolveNext(STEPS[Math.min(STEPS.indexOf(p) + 1, STEPS.length - 1)]) || p);
  };

  const backPage = () => {
    clearTimeout(advanceRef.current);
    setErr("");
    setPage((p) => resolveBack(STEPS[Math.max(STEPS.indexOf(p) - 1, 0)]) || p);
  };

  const pick = (setter, value, from, to) => {
    setter(value);
    setErr("");
    later(() => setPage((p) => (p === from ? resolveNext(to) : p)));
  };

  const applyStatus = (data) => {
    if (!data) return;
    if (data.left !== undefined && data.left !== null) setGenLeft(data.left);
    const vs = Array.isArray(data.variants) ? data.variants : [];
    if (vs.length) setVariants(vs.slice(-2));
    if (data.pick !== undefined) setPickIdx(Number(data.pick) || 0);
    setDesc(data.lyrics || "");
    setDraftTitle(data.title || "");
    setTextReady(true);
    setGenerated(true);
  };

  const switchVariant = async (idx) => {
    if (idx === pickIdx) return;
    const v = variants[idx];
    if (!v) return;
    setPickIdx(idx);
    setDesc(v.text || "");
    setDraftTitle(v.title || "");
    try {
      await api.post("/public/app/gen-lyrics/pick", {
        order_id: draftOrderRef.current || initialDraft?.id || null,
        idx,
        client_id: getClientId(),
      });
    } catch { /* текст уже переключён локально */ }
  };

  const genText = async () => {
    if (busy) return;
    if (!hasBalance) {
      setErr("Генерація тексту доступна після оплати пакета.");
      return;
    }
    if (desc.trim().length < 50) {
      setErr("Опишіть історію детальніше — мінімум 50 символів.");
      return;
    }
    setBusy("gen-text");
    setErr("");
    try {
      const cid = getClientId();
      // Регенерация всегда идёт от ИСТОРИИ юзера, а не от прошлого текста.
      const facts = (styleNote ? styleNote + ". " : "") + (story.trim() || desc.trim());
      const draftRes = await api.post("/public/landing/unpaid", {
        notify: false,
        genre: genre || "pop",
        voice: voice || "female",
        description: facts,
        client_id: cid,
        mode: "app",
        event: event || "",
        event_text: eventText || "",
        mood: mood || "",
        song_language: lang || "uk",
        tg_id: getTgUser()?.id || null,
        order_id: draftOrderRef.current || initialDraft?.id || null,
      });
      const oid = draftRes.data?.id || draftOrderRef.current || initialDraft?.id || null;
      if (!oid) throw new Error("no-draft");
      draftOrderRef.current = oid;
      const genRes = await api.post("/public/app/gen-lyrics", {
        event: event || "nooccasion",
        event_text: eventText || "",
        genre: genre || "pop",
        mood: mood || "",
        song_language: lang || "uk",
        voice: voice || "female",
        facts,
        tg_id: getTgUser()?.id || null,
        client_id: cid,
        order_id: oid,
      });
      if (genRes.data?.left !== undefined && genRes.data.left !== null) {
        setGenLeft(genRes.data.left);
      }
      const started = Date.now();
      while (Date.now() - started < 120000) {
        await new Promise((r) => setTimeout(r, 2500));
        const r = await api.get("/public/app/gen-lyrics/status", { params: { order_id: oid } });
        const st = r.data?.state;
        if (st === "ready") {
          applyStatus(r.data);
          trackApp("створено опис (згенеровано текст)", (r.data.title || "").slice(0, 80));
          break;
        }
        if (st === "error") {
          setErr("Не вдалося згенерувати текст. Спробуйте ще раз або напишіть вручну.");
          break;
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "payment-required") {
        setErr("Генерація тексту доступна після оплати пакета.");
      } else if (detail === "lyrics-limit" || detail === "day-limit" || detail === "text-limit") {
        setGenLeft(0);
        setErr("Ліміт генерацій тексту вичерпано. Придбайте пакет — і ліміт оновиться.");
      } else if (detail === "facts-too-short") {
        setErr("Опишіть історію детальніше — мінімум 50 символів.");
      } else if (detail === "rate-limit" || detail === "text-limit") {
        setErr("Забагато генерацій зараз. Спробуйте трохи пізніше.");
      } else {
        setErr("Не вдалося згенерувати текст. Спробуйте ще раз або напишіть вручну.");
      }
    } finally { setBusy(""); setUploadPct(null); }
  };

  const pickReceipt = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp", "application/pdf"].includes(f.type) || f.size > MAX_RECEIPT) {
      setErr("Чек має бути фото (jpg, png, webp) або PDF до 10 МБ.");
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setReceipt(f);
    setPreview(URL.createObjectURL(f));
    setErr("");
    // Ужимаем фото в фоне: большие скрины чаще обрываются при отправке.
    const small = await compressReceipt(f);
    if (small !== f) setReceipt(small);
  };

  const copyCard = async () => {
    try {
      await navigator.clipboard.writeText(card);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { setCopied(false); }
  };

  const [balanceModal, setBalanceModal] = useState(false);

  const submitFromBalance = async () => {
    if (busy) return;
    setErr("");
    setBalanceModal(false);
    setBusy("balance");
    try {
      const r = await api.post("/public/app/order-balance", {
        client_id: getClientId(),
        genre,
        voice,
        description: (styleNote ? styleNote + ". " : "") + (story.trim() || desc.trim()),
        event: event || "",
        event_text: eventText || "",
        mood: mood || "",
        mood_text: mood === "custom" ? moodText.trim() : "",
        genre_text: genre === "custom" ? genreText.trim() : "",
        genre_mix: genre === "mix" ? genreMix : [],
        song_language: lang || "",
        ready_lyrics: textReady ? desc.trim() : "",
        ready_title: generated ? draftTitle : "",
      });
      submittedRef.current = true;
      setOrderNumber(r.data?.number);
      setPage("done");
      trackApp("замовлення з балансу", `#${r.data?.number || ""}`);
      onBalanceUsed?.();
      onOrdered?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "no-balance") setErr("На балансі немає генерацій.");
      else if (detail === "mix-needs-two") {
        setErr("Для міксу оберіть 2 жанри.");
        setMixDraft(genreMix);
        setPage("genre");
        setMixOpen(true);
      } else if (detail === "unauthorized") setErr("Відкрийте застосунок через Telegram-бота.");
      else setErr("Не вдалося створити з балансу. Спробуйте ще раз.");
    } finally { setBusy(""); setUploadPct(null); }
  };

  const submit = async () => {
    if (busy || !receipt) return;
    if (initialTopup) {
      setBusy("submit");
      setErr("");
      setUploadPct(0);
      try {
        const fd = new FormData();
        fd.append("package_key", activePkg?.key || "");
        fd.append("regen_addon", regen ? "1" : "");
        fd.append("contact", contact.trim());
        fd.append("client_id", getClientId());
        fd.append("tg_id", getTgUser()?.id ? String(getTgUser().id) : "");
        fd.append("receipt", receipt);
        const r = await postFormWithRetry("/public/app/topup", fd, { onProgress: setUploadPct });
        submittedRef.current = true;
        setOrderNumber(r.data?.number);
        setPage("done");
        trackApp("поповнення балансу", `#${r.data?.number || ""} · ${activePkg?.name || ""}`);
        onOrdered?.();
      } catch (e) {
        const detail = e?.response?.data?.detail || "";
        if (detail === "receipt-invalid") setErr("Чек має бути фото (jpg, png, webp) або PDF до 10 МБ.");
        else if (detail === "unauthorized") setErr("Відкрийте застосунок через Telegram-бота.");
        else setErr("Не вдалося відправити. Спробуйте ще раз.");
      } finally { setBusy(""); setUploadPct(null); }
      return;
    }
    if (desc.trim().length < minLen) {
      setErr(textReady
        ? "Готовий текст занадто короткий — мінімум 40 символів."
        : "Опишіть пісню детальніше — мінімум 25 символів.");
      return;
    }
    setBusy("submit");
    setErr("");
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("genre", genre);
      fd.append("voice", voice);
      fd.append("description", (styleNote ? styleNote + ". " : "") + (story.trim() || desc.trim()));
      fd.append("event", event || "");
      fd.append("event_text", eventText || "");
      fd.append("mood", mood || "");
      fd.append("mood_text", mood === "custom" ? moodText.trim() : "");
      fd.append("genre_text", genre === "custom" ? genreText.trim() : "");
      fd.append("genre_mix", genre === "mix" ? JSON.stringify(genreMix) : "");
      fd.append("song_language", lang || "");
      fd.append("package_key", activePkg?.key || "");
      fd.append("regen_addon", regen ? "1" : "");
      fd.append("ready_lyrics", textReady ? desc.trim() : "");
      fd.append("contact", contact.trim());
      fd.append("client_id", getClientId());
      fd.append("tg_id", getTgUser()?.id ? String(getTgUser().id) : "");
      fd.append("receipt", receipt);
      const r = await postFormWithRetry("/public/app/order", fd, { onProgress: setUploadPct });
      submittedRef.current = true;
      setOrderNumber(r.data?.number);
      setPage("done");
      trackApp("замовлення відправлено з міні-аппа", `#${r.data?.number || ""} · ${activePkg?.name || ""}`);
      onOrdered?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "description-too-short") {
        setErr("Опишіть пісню детальніше — мінімум 25 символів.");
      } else if (detail === "receipt-invalid") {
        setErr("Чек має бути фото (jpg, png, webp) або PDF до 10 МБ.");
      } else if (detail === "mix-needs-two") {
        setErr("Для міксу оберіть 2 жанри.");
        setMixDraft(genreMix);
        setPage("genre");
        setMixOpen(true);
      } else {
        setErr("Не вдалося надіслати заявку. Перевірте зв'язок і спробуйте ще раз — або надішліть чек у підтримку через бота.");
      }
    } finally { setBusy(""); setUploadPct(null); }
  };

  const title = {
    event: c.b_ev_title,
    genre: c.b_genre_title,
    mood: c.b_mood_title,
    lang: c.b_lang_title,
    voice: c.b_voice_title,
    text: textReady ? c.desc_own_title : c.desc_title,
    pkg: initialTopup ? "Поповнення балансу" : "Обери свій тариф",
    pay: initialTopup ? "Оплата поповнення" : "Оплатити пісню",
  }[page];

  const sub = {
    event: c.b_ev_sub,
    genre: c.b_genre_sub,
    mood: c.b_mood_sub,
    lang: c.b_lang_sub,
    voice: c.b_voice_sub,
    text: textReady ? c.desc_own_sub : c.desc_sub,
    pkg: initialTopup
      ? "Оберіть тариф — пісні зарахуються на баланс після перевірки чека."
      : "Оберіть пакет — генерації не згорають і залишаються на балансі.",
    pay: initialTopup
      ? "Переведіть гроші та прикріпіть скріншот чека — пісні зарахуються на баланс після перевірки."
      : "Переведіть гроші та прикріпіть скріншот чека — пісня почне генеруватись автоматично після перевірки.",
  }[page];

  const allGenres = catGenreList;

  const pickExtraGenre = (key) => {
    setGenre(key);
    setGenreMix([]);
    setErr("");
    setExtraOpen(false);
    setPage(resolveNext("mood"));
  };

  const toggleMixGenre = (key) => {
    // Меняем только локальный выбор в модалке — состояние применит «Готово».
    setMixDraft((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= 2) return [prev[1], key];
      return [...prev, key];
    });
  };

  const confirmMix = () => {
    if (mixDraft.length !== 2) return;
    setGenreMix(mixDraft);
    setGenre("mix");
    setErr("");
    setMixOpen(false);
    setPage(resolveNext("mood"));
    // Выбор сохраняем сразу — не ждём тарифа/оплаты, иначе теряется между сессиями.
    setTimeout(() => saveDraftRef.current(false), 0);
  };

  const submitCustom = () => {
    if (modal === "event") {
      if (eventText.trim().length < 3) { setErr("Мінімум 3 символи"); return; }
      setModal(null); setPage(resolveNext("genre"));
    } else if (modal === "genre") {
      if (genreText.trim().length < 3) { setErr("Мінімум 3 символи"); return; }
      setModal(null); setPage(resolveNext("mood"));
    } else if (modal === "mood") {
      if (moodText.trim().length < 2) { setErr("Мінімум 2 символи"); return; }
      setModal(null); setPage("lang");
    }
  };

  return (
    <div className="epw" data-testid="app-create-wizard">
      <WizardHeader step={Math.min(stepIdx + 1, 7)} total={7} showProgress={page !== "done"}
        canBack={page !== "done" && (initialTopup ? page === "pay" : page !== "event")}
        onBack={backPage} onClose={onClose} />

      <div className="epw-body">
        {!cat && page !== "done" && (
          <div className="epw-loader" role="status" aria-label="Завантаження"><i /></div>
        )}

        {cat && page !== "done" && !(page === "text" && (generated || busy === "gen-text")) && (
          <h2 className="epw-title epw-h2">{title}</h2>
        )}
        {cat && page !== "done" && !(page === "text" && (generated || busy === "gen-text")) && <p className="epw-sub">{sub}</p>}
        {page === "genre" && genre === "mix" && genreMix.length > 0 && (
          <div className="epw-mix-chips" data-testid="mix-chips">
            {genreMix.map((k) => {
              const item = [...(cat?.genres_main || []), ...(cat?.genres_extra || [])]
                .find((g) => g.key === k);
              return (
                <span key={k} className="epw-mix-chip">
                  {item?.ua || k}
                  <button type="button" aria-label="Прибрати"
                    onClick={() => setGenreMix((prev) => prev.filter((x) => x !== k))}>×</button>
                </span>
              );
            })}
          </div>
        )}

        {cat && page === "event" && (
          <div className="epw-fade">
            {events.length === 0 ? (
              <p className="epw-error">Не вдалося завантажити події. Спробуйте пізніше.</p>
            ) : (
              <div className="epw-ev-grid">
                {events.map((e, i) => (
                  <OccasionCard key={e.key} eventKey={e.key} label={stripEmoji(e.ua || e.key)} delay={i * 25}
                    testId={`wizard-event-${e.key}`} selected={event === e.key}
                    onPick={(key) => {
                      setEvent(key);
                      setErr("");
                      if (key === "custom") { setModal("event"); return; }
                      later(() => setPage((p) => (p === "event" ? resolveNext("genre") : p)));
                    }} />
                ))}
              </div>
            )}
          </div>
        )}

        {cat && page === "genre" && (
          <div className="epw-fade">
            <div className="epw-grid-2">
              {SOUND_KEYS.map((key, i) => {
                const s = (c.sounds || [])[i] || { t: key, d: "" };
                return (
                  <button key={key} data-testid={`wizard-genre-${key}`}
                    onClick={() => pick(setGenre, key, "genre", "mood")}
                    className={"epw-card" + (genre === key ? " active" : "")}>
                    <span className="epw-ico">{SOUND_ICONS[i]}</span>
                    <span className="epw-card-txt">
                      <span className="epw-card-t">{s.t}</span>
                      <span className="epw-card-d">{s.d}</span>
                    </span>
                  </button>
                );
              })}
              <button data-testid="wizard-genre-custom"
                onClick={() => { setGenre("custom"); setGenreMix([]); setErr(""); setModal("genre"); }}
                className={"epw-card" + (genre === "custom" ? " active" : "")}>
                <span className="epw-ico">✍️</span>
                <span className="epw-card-txt">
                  <span className="epw-card-t">{c.b_genre_custom}</span>
                  <span className="epw-card-d">{c.b_custom_sub}</span>
                </span>
              </button>
              <button data-testid="wizard-genre-extra"
                onClick={() => { setErr(""); setExtraOpen(true); }}
                className="epw-card">
                <span className="epw-ico">🎛</span>
                <span className="epw-card-txt">
                  <span className="epw-card-t">Додаткові жанри</span>
                  <span className="epw-card-d">Ще {(cat?.genres_extra || []).length} стилів на вибір</span>
                </span>
              </button>
              <button data-testid="wizard-genre-mix"
                onClick={() => { setErr(""); setMixDraft(genreMix); setMixOpen(true); }}
                className={"epw-card epw-card-mix" + (genre === "mix" ? " active" : "")}>
                <span className="epw-ico">🧬</span>
                <span className="epw-card-txt">
                  <span className="epw-card-t">Мікс жанрів</span>
                  <span className="epw-card-d">Поєднайте 2 стилі в одній пісні</span>
                </span>
              </button>
            </div>
            {canNext && (
              <button data-testid="wizard-genre-next" onClick={nextPage}
                className="epw-cta" style={{ marginTop: 14 }}>
                Далі <img className="epw-btn-ico" src="/design/icons-lg/forward.svg" alt="" />
              </button>
            )}
          </div>
        )}

        {cat && page === "mood" && (
          <div className="epw-fade">
            <div className="epw-grid-2 epw-grid-4">
              {moods.map((m) => (
                <button key={m.key} data-testid={`wizard-mood-${m.key}`}
                  onClick={() => pick(setMood, m.key, "mood", "lang")}
                  className={"epw-chip" + (mood === m.key ? " active" : "")}>
                  {m.ua || m.en || m.key}
                </button>
              ))}
              <button data-testid="wizard-mood-custom"
                onClick={() => { setMood("custom"); setErr(""); setModal("mood"); }}
                className={"epw-chip" + (mood === "custom" ? " active" : "")}>
                ✍️ Свій настрій
              </button>
            </div>
          </div>
        )}

        {cat && page === "lang" && (
          <div className="epw-fade">
            <div className="epw-grid-2">
              {langs.map((l) => (
                <button key={l.key} data-testid={`wizard-lang-${l.key}`}
                  onClick={() => pick(setLang, l.key, "lang", "voice")}
                  className={"epw-card" + (lang === l.key ? " active" : "")}>
                  <span className="epw-ico">{LANG_FLAGS[l.key] || "🌐"}</span>
                  <span className="epw-card-txt">
                    <span className="epw-card-t">{stripEmoji(l.ua || l.en || l.key)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {cat && page === "voice" && (
          <div className="epw-fade">
            <div className="epw-grid-2 epw-grid-4">
              {voices.map((v) => (
                <button key={v.key} data-testid={`wizard-voice-${v.key}`}
                  onClick={() => pick(setVoice, v.key, "voice", "text")}
                  className={"epw-vchip" + (voice === v.key ? " active" : "")}>
                  <span className="epw-vico">{VOICE_EMOJI[v.key] || "🎤"}</span>
                  <span className="epw-vlabel">{stripEmoji(v.ua || v.en || v.key)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {cat && page === "text" && (
          <div className="epw-fade">
            {busy === "gen-text" ? (
              <div className="epw-genstatus" role="status">
                <span className="epw-genwave"><i /><i /><i /><i /><i /><i /><i /></span>
                <div className="epw-genstatus-t">Складаємо текст…</div>
                <div className="epw-genstatus-d">Зазвичай це займає до 30 секунд</div>
              </div>
            ) : (
              <>
                <div className="epw-toggle-row">
                  <div className="epw-toggle-txt">
                    <div className="epw-toggle-t">У мене є готовий текст</div>
                  </div>
                  <button type="button" role="switch" aria-checked={textReady} data-testid="wizard-ready-toggle"
                    onClick={() => {
                      if (textReady) {
                        setTextReady(false);
                        setGenerated(false);
                        setErr("");
                      } else {
                        setModal("own-text");
                      }
                    }}
                    className={"epw-switch" + (textReady ? " on" : "")}>
                    <i />
                  </button>
                </div>
                {!textReady && <p className="epw-gen-note">{c.desc_gen_note}</p>}
                {generated && variants.length >= 2 && (
                  <div className="epw-variants" data-testid="lyrics-variants" role="tablist">
                    {variants.map((v, i) => (
                      <button key={i} type="button" role="tab" aria-selected={pickIdx === i}
                        data-testid={`lyrics-variant-${i}`}
                        onClick={() => switchVariant(i)}
                        className={"epw-variant" + (pickIdx === i ? " active" : "")}>
                        <img className="epw-variant-ico" src="/design/icons-lg/music.svg" alt="" />
                        Текст {i + 1}
                      </button>
                    ))}
                  </div>
                )}
                {generated && (
                  <InfoGlassBadge className="epw-text-badge">
                    Ваш текст готовий. Внесіть корективи, якщо потрібно. Якщо текст звучить незвично — нічого страшного, це не вірш: ми знаємо, як його заспівати.
                  </InfoGlassBadge>
                )}
                <textarea data-testid="wizard-desc" value={desc} rows={11} maxLength={3000}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDesc(v);
                    if (!textReady) setStory(v);
                    setErr("");
                  }}
                  placeholder={textReady ? "[Verse]\nВставте свій текст пісні…" : c.desc_ph}
                  className="epw-input epw-textarea" />
                <div className="epw-counter">
                  <span>{desc.trim().length}/3000</span>
                  <span>{generated && genLeft !== null ? `Залишилось генерацій: ${genLeft}` : `мін. ${minLen} символів`}</span>
                </div>
                {err && <p className="epw-error">{err}</p>}
                {textReady ? (
                  <>
                    <button data-testid="wizard-next" onClick={nextPage} disabled={!canNext || !!busy} className="epw-cta">
                      Далі <img className="epw-btn-ico" src="/design/icons-lg/forward.svg" alt="" />
                    </button>
                    {generated && hasBalance && (
                      <button data-testid="wizard-regen" onClick={genText}
                        disabled={!!busy || desc.trim().length < 50 || genLeft === 0}
                        className="epw-regen">
                        <img className="epw-regen-ico" src="/design/icons-lg/refresh.svg" alt="" />
                        Згенерувати ще раз{genLeft !== null ? ` · залишилось ${genLeft}` : ""}
                      </button>
                    )}
                  </>
                ) : hasBalance ? (
                  <button data-testid="wizard-gen-text" onClick={genText}
                    disabled={!!busy || desc.trim().length < 50 || genLeft === 0}
                    className="epw-blue">
                    <img className="epw-btn-ico" src="/design/icons-lg/idea.svg" alt="" />
                    Далі (генерація тексту){genLeft !== null ? `: ${genLeft}` : ""}
                  </button>
                ) : (
                  <button data-testid="wizard-next" onClick={nextPage}
                    disabled={!canNext || !!busy} className="epw-cta">
                    Далі <img className="epw-btn-ico" src="/design/icons-lg/forward.svg" alt="" />
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {cat && page === "pkg" && (
          <div className="epw-fade epw-pay">
            <InfoGlassBadge>При покупці кількох пісень вони залишаються на балансі, поки ви їх не використаєте.</InfoGlassBadge>
            {!initialTopup && balance > 0 && (
              <div className="epw-balance-row">
                <button type="button" data-testid="wizard-balance"
                  onClick={() => setBalanceModal(true)} disabled={!!busy}
                  className="epw-balance-btn">
                  {busy === "balance"
                    ? <Loader2 size={15} className="animate-spin" />
                    : <Check size={15} strokeWidth={3} />}
                  Створити з балансу ({balance})
                </button>
              </div>
            )}
            <div className="pkg-grid epw-pkg-grid">
              {(packages.length ? packages : [FALLBACK_PKG]).map((p) => (
                <PackageCard key={p.key || "fallback"} p={p}
                  active={(activePkg?.key || "") === (p.key || "")}
                  onPick={() => { setPkgKey(p.key); setErr(""); setPkgModal(p); }} />
              ))}
              <PremiumCard />
            </div>
          </div>
        )}

        {cat && page === "pay" && (
          <div className="epw-fade epw-pay">
            <div className="epw-glass epw-panel epw-panel-order">
              <div className="epw-order-line">
                <span className="epw-order-name">{activePkg?.name_ua || FALLBACK_PKG.name_ua}</span>
                <span className="epw-order-price">{activePkg ? Number(activePkg.price_uah) || 0 : 0} ₴</span>
              </div>
              {regen && (
                <div className="epw-order-line epw-order-line-sub">
                  <span className="epw-order-name">Перегенерація</span>
                  <span className="epw-order-price">+{REGEN_PRICE} ₴</span>
                </div>
              )}
              <div className="epw-order-total">
                <span>Разом</span>
                <span className="epw-order-total-val">{total} ₴</span>
              </div>
            </div>

            <div className="epw-glass epw-panel">
              <div className="epw-label">Картка для оплати</div>
              {cardLoading ? (
                <div className="epw-loading epw-loading-sm">
                  <Loader2 size={14} className="spin" /> Завантажую реквізити…
                </div>
              ) : card ? (
                <div className="epw-card-line">
                  <code className="epw-card-code">{card}</code>
                  <button data-testid="wizard-copy-card" onClick={copyCard} title="Копіювати" className="epw-copy">
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? "Готово" : "Копіювати"}
                  </button>
                </div>
              ) : (
                <p className="epw-row-d">Реквізити з'являться тут. Якщо їх немає — напишіть у підтримку.</p>
              )}

              <div className="epw-field">
                <div className="epw-label">{c.pay_receipt}</div>
                <input ref={fileRef} data-testid="wizard-receipt" type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={pickReceipt} />
                <button onClick={() => fileRef.current?.click()} className="epw-drop">
                  {preview ? (
                    receipt && receipt.type === "application/pdf" ? (
                      <span className="epw-drop-in">
                        <FileText size={26} /> {receipt.name}
                        <span className="epw-drop-hint">PDF-чек · до 10 МБ</span>
                      </span>
                    ) : (
                      <img src={preview} alt="" className="epw-preview" />
                    )
                  ) : (
                    <span className="epw-drop-in">
                      <FileImage size={26} /> Обрати фото чека
                      <span className="epw-drop-hint">jpg, png, webp або PDF · до 10 МБ</span>
                    </span>
                  )}
                </button>
                {preview && (
                  <button onClick={() => fileRef.current?.click()} className="epw-change">Змінити фото</button>
                )}
              </div>

              <InfoGlassBadge className="epw-pay-badge">
                Перевірка триває від 10 секунд до 5 хв. З цього меню можна виходити — ваше замовлення буде доступне в розділі «Мої пісні».
              </InfoGlassBadge>
              {err && <p className="epw-error">{err}</p>}
              <button data-testid="wizard-submit" onClick={submit}
                disabled={!!busy || !receipt || (!initialTopup && desc.trim().length < minLen)}
                className="epw-cta is-green">
                {busy === "submit" ? <Loader2 size={17} className="spin" /> : <Check size={17} />}
                {busy === "submit" ? c.submitting : c.submit}
              </button>
              {busy === "submit" && (
  <div className="w-full mt-3" data-testid="upload-progress">
    <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-300"
        style={{ width: `${uploadPct || 0}%`, background: "linear-gradient(90deg,#2BA8FF,#10B981)" }} />
    </div>
    <div className="mt-1.5 text-center text-xs text-[#9FB0C7]">
      {uploadPct >= 100 ? "Обробляємо чек…" : `Завантажуємо чек… ${uploadPct || 0}%`}
    </div>
  </div>
)}
            </div>
          </div>
        )}

        {page === "done" && (
          <div className="epw-fade epw-done">
            <div className="epw-done-ico"><Check size={38} /></div>
            <h2 className="epw-title epw-h2">Дякуємо!</h2>
            <p className="epw-sub">{initialTopup ? "Чек на перевірці" : "Ваше замовлення в роботі"}</p>
            {orderNumber ? (
              <div className="epw-done-num">{initialTopup ? "Поповнення" : "Замовлення"} <b>#{orderNumber}</b></div>
            ) : null}
            <p className="epw-done-note">
              {initialTopup
                ? "Після підтвердження оплати пісні зарахуються на ваш баланс — їх можна використати в конструкторі."
                : "Статус замовлення можна відстежувати у розділі «Мої пісні» — щойно пісня буде готова, ви отримаєте її в боті."}
            </p>
            <div className="epw-done-actions">
              <button data-testid="wizard-go-songs" onClick={() => onGoSongs?.()} className="epw-cta">
                Перейти до «Моїх пісень»
              </button>
              <button data-testid="wizard-done-close" onClick={onClose} className="epw-ghost">
                Закрити
              </button>
            </div>
          </div>
        )}
      </div>
      <OwnTextModal open={modal === "own-text"} onClose={() => setModal(null)}
        onConfirm={() => {
          setModal(null);
          setTextReady(true);
          setGenerated(false);
          setErr("");
          trackApp("конструктор: власний текст", "увімкнено");
        }} />
      <CustomModal open={modal === "event"} title="Своя подія" minLen={3} err={err} inputRef={modalInputRef}
        sub="Опишіть своїми словами, для чого пісня — ШІ врахує це у тексті."
        placeholder={c.b_ev_custom_ph}
        value={eventText} onChange={(v) => { setEventText(v); setErr(""); }}
        onClose={() => setModal(null)} onSubmit={submitCustom} />
      <CustomModal open={modal === "genre"} title={c.b_genre_custom} minLen={3} err={err} inputRef={modalInputRef}
        sub={c.b_custom_sub}
        placeholder={c.b_custom_ph}
        value={genreText} onChange={(v) => { setGenreText(v); setErr(""); }}
        onClose={() => setModal(null)} onSubmit={submitCustom} />
      <CustomModal open={modal === "mood"} title="Свій настрій" minLen={2} err={err} inputRef={modalInputRef}
        sub="Які емоції має передати пісня? Опишіть своїми словами."
        placeholder={c.b_mood_custom_ph}
        value={moodText} onChange={(v) => { setMoodText(v); setErr(""); }}
        onClose={() => setModal(null)} onSubmit={submitCustom} />
      <PkgModal pkg={pkgModal} regen={regen} onRegen={setRegen}
        onClose={() => setPkgModal(null)}
        onProceed={() => { setPkgModal(null); setPage("pay"); }} />
      <BalanceModal open={balanceModal} balance={balance} busy={busy === "balance"}
        onClose={() => setBalanceModal(false)} onConfirm={submitFromBalance} />
      <ExtraGenresModal open={extraOpen} items={cat?.genres_extra || []}
        onPick={pickExtraGenre} onClose={() => setExtraOpen(false)} />
      <MixGenresModal open={mixOpen} items={allGenres} selected={mixDraft}
        onToggle={toggleMixGenre} onConfirm={confirmMix}
        onClose={() => setMixOpen(false)} />
    </div>
  );
}
